import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Bell, BellOff, CheckCheck, Plus } from 'lucide-react'

import { SnoozeDialog } from '@/components/notifications/SnoozeDialog'
import { TaskCreateDialog } from '@/components/notifications/TaskCreateDialog'
import { TaskDetailDialog } from '@/components/notifications/TaskDetailDialog'
import { TransferDialog } from '@/components/notifications/TransferDialog'
import { NotificationFilters } from '@/components/notifications/NotificationFilters'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/contexts/AuthContext'
import { useEntity } from '@/contexts/EntityContext'
import { useCurrentPageEntity } from '@/contexts/PageEntityContext.helpers'
import { useColleagues } from '@/hooks/useAbsences'
import { useNotificationFilterPreferences } from '@/hooks/useNotificationFilterPreferences'
import {
    useNotificationList,
    useNotificationMutations,
    useUnreadCount,
} from '@/hooks/useNotifications'
import { useOnScreenToasts } from '@/hooks/useOnScreenToasts'
import type { Notification } from '@/hooks/useNotifications.types'
import { WidgetRow } from './NotificationWidgetRow'

export function NotificationCenter() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { currentEntity } = useEntity()
    const { hasPermission } = useAuth()
    const entityId = currentEntity?.entityId ?? null
    const canCreateTask = hasPermission('tasks:create')
    // Collègues (session-scopé : PAS besoin de users:read → jamais de 403 ni de
    // bouton « Déléguer » masqué à tort ; exclut soi-même + comptes de service).
    const { colleagues } = useColleagues(null, entityId)
    const canAssign = hasPermission('tasks:assign') && colleagues.length > 0
    // Clic seul (pas d'ouverture au survol) : indispensable pour que le menu de
    // report imbriqué ne déclenche pas le timer de fermeture au survol (qui rouvrait
    // puis refermait le panneau). Fermeture uniquement au clic extérieur.
    const [open, setOpen] = useState(false)
    // Filtres En retard / Urgentes — partagés (persistés) avec la page ; filtrage
    // SERVEUR sur les 8 dernières (porte sur tout le corpus, pas juste l'affichage).
    const { overdue, urgent, setOverdue, setUrgent } = useNotificationFilterPreferences()

    // Fiche de la page courante → pré-remplit le lien de « Nouvelle tâche » (WKF-013
    // §5.4) : sur une fiche restaurant/lieu/client/apprenant/formation…, la tâche
    // créée depuis la cloche est automatiquement rattachée à cette fiche.
    const pageEntity = useCurrentPageEntity()
    const [taskOpen, setTaskOpen] = useState(false)
    const [snoozeTarget, setSnoozeTarget] = useState<Notification | null>(null)
    const [transferTarget, setTransferTarget] = useState<Notification | null>(null)
    const [detailId, setDetailId] = useState<string | null>(null)

    const { data: counts, refetch: refetchCounts } = useUnreadCount(entityId)
    // Panneau : 8 dernières NON clôturées (spec §7.1), filtres appliqués côté serveur.
    const { data: list, isLoading, refetch: refetchList } = useNotificationList(entityId, {
        limit: 8,
        open_only: true,
        overdue: overdue || undefined,
        priority: urgent ? 'URGENT' : undefined,
    })
    const { markRead, markAllRead, treat, reopen } = useNotificationMutations(entityId)

    // Canal « Instantané » (onScreen) : poll de la file de toasts éphémères.
    useOnScreenToasts(entityId)

    // À l'ouverture, rafraîchir compteur + panneau ensemble (cohérence badge ↔ liste).
    useEffect(() => {
        if (open) {
            refetchCounts()
            refetchList()
        }
    }, [open, refetchCounts, refetchList])

    const unread = counts?.total ?? 0
    const items = list?.items ?? []

    // Simple clic = ouvrir le détail complet en modale (convention universelle,
    // découvrable, tactile) ; le passage à « lu » en découle. (Retour PO + revue
    // experts : le simple-clic-qui-marque-lu-sans-rien-ouvrir n'était pas intuitif.)
    const openDetail = (n: Notification) => {
        if (n.status === 'UNREAD') markRead.mutate(n.id)
        setOpen(false)
        setDetailId(n.id)
    }
    // « Choisir une date… » depuis le report rapide : ferme le panneau, ouvre la modale.
    const openPreciseSnooze = (n: Notification) => {
        setOpen(false)
        setSnoozeTarget(n)
    }
    const openTransfer = (n: Notification) => {
        setOpen(false)
        setTransferTarget(n)
    }
    const openNewTask = () => {
        setOpen(false)
        setTaskOpen(true)
    }

    // Ouverture au survol (desktop) SANS le piège du timer de fermeture : on ne
    // ferme PAS tant qu'un menu de report imbriqué est ouvert (portalisé → la souris
    // « quitte » le panneau). Fermeture par clic extérieur / Escape de toute façon.
    const hoverable = useRef(
        typeof window !== 'undefined' &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(hover: hover) and (pointer: fine)').matches,
    )
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    // Vrai le temps d'une fermeture DÉCLENCHÉE PAR LE SURVOL : dans ce cas on empêche
    // Radix de rendre le focus à la cloche, sinon ce focus() volerait le focus du menu
    // utilisateur voisin qui vient de s'ouvrir (DismissableLayer non-modal → se referme).
    // Les fermetures par clic/Échap laissent la restitution du focus normale (a11y clavier).
    const closingViaHover = useRef(false)
    const cancelClose = useCallback(() => {
        if (closeTimer.current) {
            clearTimeout(closeTimer.current)
            closeTimer.current = null
        }
    }, [])
    const openOnHover = useCallback(() => {
        if (hoverable.current) {
            cancelClose()
            setOpen(true)
        }
    }, [cancelClose])
    const scheduleClose = useCallback(() => {
        if (!hoverable.current) return
        cancelClose()
        closeTimer.current = setTimeout(() => {
            // Un menu imbriqué (report OU transfert) est ouvert (portalisé) → ne pas fermer.
            if (document.querySelector('[data-nested-menu]')) return
            closingViaHover.current = true
            setOpen(false)
        }, 180)
    }, [cancelClose])
    useEffect(() => cancelClose, [cancelClose])
    const hoverProps = { onMouseEnter: openOnHover, onMouseLeave: scheduleClose }

    return (
        <>
            {/* Popover (pas DropdownMenu) : le panneau contient des contrôles riches
                (cases à cocher, boutons, menu de report imbriqué) qui n'ont pas leur
                place dans un role=menu — d'où une navigation clavier correcte ET aucune
                fermeture intempestive quand on ouvre le menu de report imbriqué. */}
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    {/* Pas d'anneau de focus : la cloche retient le focus au retour du popover
                        → anneau sombre disgracieux sans intérêt (l'ouverture du panneau EST le
                        retour visuel). */}
                    <Button
                        {...hoverProps}
                        variant="ghost"
                        size="icon"
                        className="relative h-10 w-10 rounded-xl focus-visible:ring-0 focus-visible:ring-offset-0"
                    >
                        <Bell className="h-5 w-5" />
                        {unread > 0 && (
                            <Badge className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground">
                                {unread > 99 ? '99+' : unread}
                            </Badge>
                        )}
                        <span className="sr-only">{t('notifications.title')}</span>
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    onMouseEnter={cancelClose}
                    onMouseLeave={scheduleClose}
                    // Fermeture au survol : ne pas restituer le focus à la cloche (cela
                    // refermerait le menu utilisateur voisin fraîchement ouvert). Clic/Échap
                    // → restitution normale du focus.
                    onCloseAutoFocus={(e) => {
                        if (closingViaHover.current) e.preventDefault()
                        closingViaHover.current = false
                    }}
                    // Filet de sécurité : ne pas fermer le panneau si l'interaction vient
                    // d'un popper imbriqué (menu de report). Le menu étant un enfant React
                    // du contenu, Radix le traite déjà comme une branche, mais on garde la
                    // garde par prudence.
                    onInteractOutside={(e) => {
                        const target = (e.detail as { originalEvent?: Event })?.originalEvent?.target as HTMLElement | null
                        if (target?.closest('[data-radix-popper-content-wrapper]')) e.preventDefault()
                    }}
                    className="w-96 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl p-0"
                    align="end"
                    sideOffset={12}
                >
                    <div className="flex items-center justify-between rounded-t-xl border-b bg-muted/40 px-3 py-2 text-sm font-medium">
                        <span>{t('notifications.title')}</span>
                        {unread > 0 && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-auto px-2 py-1 text-xs font-normal text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0"
                                onClick={() => markAllRead.mutate()}
                            >
                                <CheckCheck className="mr-1 h-3.5 w-3.5" />
                                {t('notifications.markAllRead')}
                            </Button>
                        )}
                    </div>
                    {/* Div de défilement SIMPLE (pas ScrollArea Radix) : son wrapper
                        display:table étirait la ligne au titre `truncate` (nowrap) →
                        débordement horizontal coupé. Un overflow-y-auto contraint la
                        largeur à 100% → truncate / line-clamp fonctionnent. */}
                    <div className="max-h-[320px] overflow-y-auto">
                        <div className="p-1">
                            {isLoading ? (
                                <div className="space-y-2 p-1">
                                    {[0, 1, 2].map((i) => (
                                        <div key={i} className="flex gap-2 p-1">
                                            <Skeleton className="h-4 w-4 rounded-full" />
                                            <div className="flex-1 space-y-1">
                                                <Skeleton className="h-3.5 w-3/4" />
                                                <Skeleton className="h-3 w-1/2" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : items.length === 0 ? (
                                <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
                                    <div className="rounded-full bg-primary/10 p-3 text-primary">
                                        <BellOff className="h-6 w-6" />
                                    </div>
                                    <p className="text-sm font-medium">{t('notifications.empty')}</p>
                                    <p className="max-w-[16rem] text-xs text-muted-foreground">
                                        {t(
                                            'notifications.emptyHint',
                                            'Vous êtes à jour. Les nouvelles tâches et alertes apparaîtront ici.',
                                        )}
                                    </p>
                                </div>
                            ) : (
                                items.map((n) => (
                                    <WidgetRow
                                        key={n.id}
                                        n={n}
                                        entityId={entityId}
                                        canAssign={canAssign && (n.source_type === 'manual' || n.source_type === 'transfer')}
                                        onOpen={openDetail}
                                        onTreat={(id) => treat.mutate(id)}
                                        onReopen={(id) => reopen.mutate(id)}
                                        onPickDate={openPreciseSnooze}
                                        onTransfer={openTransfer}
                                    />
                                ))
                            )}
                        </div>
                    </div>
                    <div className="border-t">
                        {/* Nouvelle tâche (gauche) + filtres En retard / Urgentes (droite). */}
                        <div className="flex items-center justify-between gap-2 px-2 pt-1.5">
                            {canCreateTask ? (
                                <Button variant="ghost" size="sm" className="px-1.5 font-normal" onClick={openNewTask}>
                                    <Plus className="mr-1.5 h-4 w-4" />
                                    {t('notifications.task.newTitle', 'Nouvelle tâche')}
                                </Button>
                            ) : (
                                <span />
                            )}
                            <NotificationFilters
                                overdue={overdue}
                                urgent={urgent}
                                onOverdue={setOverdue}
                                onUrgent={setUrgent}
                                compact
                            />
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="w-full justify-center font-normal"
                            onClick={() => {
                                setOpen(false)
                                navigate('/notifications')
                            }}
                        >
                            {t('notifications.viewAll')}
                        </Button>
                    </div>
                </PopoverContent>
            </Popover>

            <TaskCreateDialog
                open={taskOpen}
                onOpenChange={setTaskOpen}
                entityId={entityId}
                linkedEntityType={pageEntity?.type ?? null}
                linkedEntityId={pageEntity?.id ?? null}
                linkedLabel={pageEntity?.label ?? null}
            />
            <SnoozeDialog
                notification={snoozeTarget}
                entityId={entityId}
                onOpenChange={(o) => !o && setSnoozeTarget(null)}
            />
            <TransferDialog
                notification={transferTarget}
                entityId={entityId}
                onOpenChange={(o) => !o && setTransferTarget(null)}
            />
            <TaskDetailDialog
                notificationId={detailId}
                entityId={entityId}
                onTransfer={setTransferTarget}
                onSnooze={setSnoozeTarget}
                onOpenChange={(o) => !o && setDetailId(null)}
            />
        </>
    )
}
